import { jsPDF } from 'jspdf';

interface CateringProviderTableRow {
  companyName: string;
  details: string[];
}

interface CateringProviderTable {
  headerLeft: string;
  headerRight: string;
  rows: CateringProviderTableRow[];
}

const PDF_FONT_FILE_NAME = 'Tajawal-Regular.ttf';
const PDF_FONT_FAMILY = 'Tajawal';
let pdfFontBinary: string | null = null;

export async function generateCateringProvidersPdfFromHtml(htmlResponse: string): Promise<void> {
  const parsedTables = parseCateringProviderTables(htmlResponse).filter((table) => table.rows.length > 0);

  if (!parsedTables.length) {
    throw new Error('Catering provider table HTML is empty');
  }

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  await ensurePdfFont(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 24;
  const tableWidth = pageWidth - margin * 2;
  const leftColWidth = Math.min(260, tableWidth * 0.38);
  const rightColWidth = tableWidth - leftColWidth;
  const cellPadding = 10;
  const headerHeight = 28;
  const lineHeight = 14;
  const detailSpacing = 6;
  const borderColor = 201;
  const headerBg = [0, 55, 100] as [number, number, number];
  let y = margin;

  const drawHeader = (table: CateringProviderTable) => {
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, tableWidth, headerHeight, 'F');
    doc.setDrawColor(borderColor);
    doc.setLineWidth(0.6);
    doc.rect(margin, y, tableWidth, headerHeight);
    doc.line(margin + leftColWidth, y, margin + leftColWidth, y + headerHeight);

    doc.setFont(PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    drawPdfLines(
      doc,
      [getPdfText(doc, table.headerLeft || 'Catering Hospitality Company name')],
      margin + cellPadding,
      y + 10,
      leftColWidth - cellPadding * 2
    );
    drawPdfLines(
      doc,
      [getPdfText(doc, table.headerRight || 'Contact details')],
      margin + leftColWidth + cellPadding,
      y + 10,
      rightColWidth - cellPadding * 2
    );
    y += headerHeight;
  };

  parsedTables.forEach((table, tableIndex) => {
    if (tableIndex > 0) {
      if (y + 20 > pageHeight - margin) {
        doc.addPage();
        y = margin;
      } else {
        y += 20;
      }
    }

    drawHeader(table);

    table.rows.forEach((row) => {
      const leftLines = getWrappedPdfLines(doc, row.companyName, leftColWidth - cellPadding * 2);
      const leftTextHeight = Math.max(lineHeight, leftLines.length * lineHeight);

      const detailBlocks = row.details.map((detail) =>
        getWrappedPdfLines(doc, detail, rightColWidth - cellPadding * 2)
      );

      let rightTextHeight = 0;
      detailBlocks.forEach((lines, index) => {
        rightTextHeight += Math.max(lineHeight, lines.length * lineHeight);
        if (index < detailBlocks.length - 1) {
          rightTextHeight += detailSpacing;
        }
      });

      const rowHeight = Math.max(56, leftTextHeight, rightTextHeight) + cellPadding * 2;
      if (y + rowHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
        drawHeader(table);
      }

      doc.setDrawColor(borderColor);
      doc.setLineWidth(0.6);
      doc.rect(margin, y, tableWidth, rowHeight);
      doc.line(margin + leftColWidth, y, margin + leftColWidth, y + rowHeight);

      doc.setFont(PDF_FONT_FAMILY, 'bold');
      doc.setFontSize(10);
      doc.setTextColor(68, 68, 68);
      drawPdfLines(doc, leftLines, margin + cellPadding, y + 14, leftColWidth - cellPadding * 2);

      doc.setFont(PDF_FONT_FAMILY, 'normal');
      doc.setTextColor(90, 90, 90);
      let currentY = y + 16;
      detailBlocks.forEach((lines, index) => {
        drawPdfLines(doc, lines, margin + leftColWidth + cellPadding, currentY, rightColWidth - cellPadding * 2);
        currentY += Math.max(lineHeight, lines.length * lineHeight);
        if (index < detailBlocks.length - 1) {
          currentY += detailSpacing;
        }
      });

      y += rowHeight;
    });
  });

  doc.save('catering-providers.pdf');
}

function parseCateringProviderTables(htmlResponse: string): CateringProviderTable[] {
  const parser = new DOMParser();
  const parsed = parser.parseFromString(htmlResponse, 'text/html');

  return Array.from(parsed.querySelectorAll('table')).map((table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) {
      return { headerLeft: '', headerRight: '', rows: [] };
    }

    const headerCells = Array.from(rows[0].querySelectorAll('th,td'));
    const dataRows = rows
      .slice(1)
      .map((row) => {
        const cells = Array.from(row.querySelectorAll('td'));
        if (cells.length < 2) {
          return null;
        }

        const companyName = normalizePdfText(cells[0].textContent || '');
        const detailLines = extractPdfDetailLines(cells[1]);
        if (!companyName && !detailLines.length) {
          return null;
        }

        return { companyName, details: detailLines };
      })
      .filter((row): row is CateringProviderTableRow => !!row);

    return {
      headerLeft: normalizePdfText(headerCells[0]?.textContent || ''),
      headerRight: normalizePdfText(headerCells[1]?.textContent || ''),
      rows: dataRows
    };
  });
}

function extractPdfDetailLines(cell: Element): string[] {
  const blocks = Array.from(cell.querySelectorAll(':scope > div'));
  const sourceBlocks = blocks.length ? blocks : [cell];
  const details: string[] = [];

  sourceBlocks.forEach((block) => {
    const paragraphs = Array.from(block.querySelectorAll(':scope > p'));
    if (!paragraphs.length && block === cell) {
      const fallback = normalizePdfText((block as HTMLElement).innerText || block.textContent || '');
      if (fallback) {
        details.push(fallback);
      }
      return;
    }

    paragraphs.forEach((paragraph) => {
      const text = normalizePdfText((paragraph as HTMLElement).innerText || paragraph.textContent || '');
      if (text) {
        details.push(text);
      }
    });
  });

  return details;
}

function normalizePdfText(value: string): string {
  const normalized = String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([|,:;])/g, '$1')
    .trim();

  return tryDecodeMisencodedUtf8(normalized);
}

async function ensurePdfFont(doc: jsPDF): Promise<void> {
  if (!pdfFontBinary) {
    const response = await fetch('/fonts/Tajawal-Regular.ttf');
    if (!response.ok) {
      throw new Error('Unable to load PDF font');
    }
    pdfFontBinary = arrayBufferToBinaryString(await response.arrayBuffer());
  }

  try {
    doc.addFileToVFS(PDF_FONT_FILE_NAME, pdfFontBinary);
  } catch {}

  try {
    doc.addFont(PDF_FONT_FILE_NAME, PDF_FONT_FAMILY, 'normal');
  } catch {}

  try {
    doc.addFont(PDF_FONT_FILE_NAME, PDF_FONT_FAMILY, 'bold');
  } catch {}

  doc.setFont(PDF_FONT_FAMILY, 'normal');
}

function arrayBufferToBinaryString(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return binary;
}

function getWrappedPdfLines(doc: jsPDF, value: string, width: number): string[] {
  const wrapped = doc.splitTextToSize(getPdfText(doc, value), width);
  return Array.isArray(wrapped) ? wrapped : [wrapped];
}

function getPdfText(doc: jsPDF, value: string): string {
  const normalized = normalizePdfText(value);
  if (containsArabicText(normalized) && typeof doc.processArabic === 'function') {
    return doc.processArabic(normalized);
  }

  return normalized;
}

function drawPdfLines(doc: jsPDF, lines: string[], x: number, y: number, width: number): void {
  const text = lines.join(' ');
  const hasArabic = containsArabicText(text);
  doc.setR2L(hasArabic);
  doc.text(lines, hasArabic ? x + width : x, y, {
    align: hasArabic ? 'right' : 'left',
    baseline: 'top'
  });
  doc.setR2L(false);
}

function containsArabicText(value: string): boolean {
  return /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff]/.test(value);
}

function tryDecodeMisencodedUtf8(value: string): string {
  if (!/[���]/.test(value) || containsArabicText(value)) {
    return value;
  }

  try {
    const bytes = Uint8Array.from(Array.from(value).map((char) => char.charCodeAt(0) & 0xff));
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes).trim();
    return decoded && !decoded.includes('\ufffd') ? decoded : value;
  } catch {
    return value;
  }
}
